import { toPng } from "html-to-image";
import { PDFDocument } from "pdf-lib";

/** Pixel ratio that makes the exported picture about 300 DPI when printed 8.5 in wide (capped so phones don't run out of memory). */
export function printPixelRatio(el: HTMLElement, printWidthIn = 8.5, dpi = 300): number {
  const w = el.offsetWidth || 500;
  return Math.min(6, Math.max(2, (printWidthIn * dpi) / w));
}

/**
 * Run `fn` with `el` sized to its own content. A preview card that is a CSS-grid / flex item next to a tall controls
 * column is stretched to the row height (align-self: stretch), and html-to-image would then export that whole stretched
 * box as a mostly empty band (B-0004: 2550x9031 PNG, 5.25x17.9in bleed PDF). `align-self: start` + `height: auto` makes
 * the box shrink to its content; the previous inline values are restored afterwards.
 */
export async function withNaturalHeight<T>(el: HTMLElement, fn: () => Promise<T>): Promise<T> {
  const prev = { alignSelf: el.style.alignSelf, height: el.style.height };
  el.style.alignSelf = "start";
  el.style.height = "auto";
  try { return await fn(); } finally { el.style.alignSelf = prev.alignSelf; el.style.height = prev.height; }
}

export async function elementToPngBlob(el: HTMLElement, opts: { printWidthIn?: number } = {}): Promise<Blob> {
  return withNaturalHeight(el, async () => {
    const url = await toPng(el, { pixelRatio: printPixelRatio(el, opts.printWidthIn), cacheBust: true });
    return (await fetch(url)).blob();
  });
}

/**
 * Split a tall list into pages. `rowTops` are each row's top offset (css px, ascending, first is 0) and `total` the full
 * height; a page never exceeds `maxPage` unless a single row is taller, and breaks fall on row boundaries.
 */
export function planPages(rowTops: number[], total: number, maxPage: number): Array<[number, number]> {
  if (!(total > 0) || !(maxPage > 0) || total <= maxPage) return [[0, Math.max(0, total)]];
  const bounds = Array.from(new Set(rowTops.filter((t) => t > 0 && t < total))).sort((x, y) => x - y).concat(total);
  const pages: Array<[number, number]> = [];
  let start = 0, fit = 0, i = 0;
  while (i < bounds.length) {
    const b = bounds[i];
    if (b - start <= maxPage) { fit = b; i++; continue; }
    if (fit > start) { pages.push([start, fit]); start = fit; continue; }   // re-test b against the new page
    pages.push([start, b]); start = b; fit = b; i++;                        // one row taller than a page: own page
  }
  if (start < total) pages.push([start, total]);
  return pages;
}

/** Max css height of one page for a list `widthPx` wide: Letter-ish proportions (about 1.3 x width), so parts print and share well. */
export const listPageMaxHeight = (widthPx: number) => Math.round(widthPx * 1.3);

/** A tall list as several shareable PNGs (one when it already fits), cut between rows. */
export async function listToPagedPngBlobs(el: HTMLElement, rows: HTMLElement[], opts: { pixelRatio?: number; backgroundColor?: string } = {}): Promise<Blob[]> {
  const ratio = opts.pixelRatio ?? 2;
  const url = await toPng(el, { pixelRatio: ratio, cacheBust: true, backgroundColor: opts.backgroundColor ?? "#ffffff" });
  const full = await createImageBitmap(await (await fetch(url)).blob());
  const base = el.getBoundingClientRect().top;
  const tops = rows.map((r) => r.getBoundingClientRect().top - base).sort((a, b) => a - b);
  const pages = planPages(tops, full.height / ratio, listPageMaxHeight(full.width / ratio));
  const out: Blob[] = [];
  for (const [y0, y1] of pages) {
    const c = document.createElement("canvas");
    c.width = full.width; c.height = Math.max(1, Math.round((y1 - y0) * ratio));
    const g = c.getContext("2d")!;
    g.fillStyle = opts.backgroundColor ?? "#ffffff"; g.fillRect(0, 0, c.width, c.height);
    g.drawImage(full, 0, Math.round(y0 * ratio), full.width, c.height, 0, 0, full.width, c.height);
    out.push(await new Promise<Blob>((res, rej) => c.toBlob((x) => (x ? res(x) : rej(new Error("canvas"))), "image/png")));
  }
  return out;
}

/** One Letter page per picture, each scaled to fit inside the margin (top-aligned so a list reads like a document). */
export async function pngBlobsToLetterPdfBytes(pngs: Blob[], marginIn = 0.5): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const [pw, ph] = PAGES.letter;
  const m = marginIn * 72;
  for (const b of pngs) {
    const img = await pdf.embedPng(new Uint8Array(await b.arrayBuffer()));
    const s = Math.min((pw - 2 * m) / img.width, (ph - 2 * m) / img.height);
    const w = img.width * s, h = img.height * s;
    pdf.addPage([pw, ph]).drawImage(img, { x: (pw - w) / 2, y: ph - m - h, width: w, height: h });
  }
  return pdf.save();
}

const PAGES = { letter: [612, 792], a4: [595.28, 841.89] } as const;

/** A real PDF (not a print dialog): the element as a sharp image centred on a Letter or A4 page, scaled to fit inside the margin. */
export async function elementToPdfBytes(el: HTMLElement, page: "letter" | "a4" = "letter", marginIn = 0.5): Promise<Uint8Array> {
  const png = new Uint8Array(await (await elementToPngBlob(el)).arrayBuffer());
  const pdf = await PDFDocument.create();
  const img = await pdf.embedPng(png);
  const [pw, ph] = PAGES[page];
  const m = marginIn * 72;
  const s = Math.min((pw - 2 * m) / img.width, (ph - 2 * m) / img.height);
  const w = img.width * s, h = img.height * s;
  pdf.addPage([pw, ph]).drawImage(img, { x: (pw - w) / 2, y: (ph - h) / 2, width: w, height: h });
  return pdf.save();
}

export interface BleedLayout { pageW: number; pageH: number; trimW: number; trimH: number; bleed: number; bleedPx: number; trimPxW: number; trimPxH: number }
/** All sizes in PDF points except the px fields. `pxPerIn` is the picture's resolution at the chosen trim width. */
export function bleedLayout(imgW: number, imgH: number, trimWidthIn: number, bleedIn = 0.125): BleedLayout {
  const trimHeightIn = trimWidthIn * (imgH / imgW);
  const pxPerIn = imgW / trimWidthIn;
  return {
    trimW: trimWidthIn * 72, trimH: trimHeightIn * 72, bleed: bleedIn * 72,
    pageW: (trimWidthIn + 2 * bleedIn) * 72, pageH: (trimHeightIn + 2 * bleedIn) * 72,
    bleedPx: Math.round(bleedIn * pxPerIn), trimPxW: imgW, trimPxH: imgH,
  };
}

/**
 * Print-shop PDF: the page is the trim size plus bleed on every side, with TrimBox and BleedBox set. The bleed band is made
 * by repeating the design's outermost pixel row/column outward, which carries solid colours, gradients and full-bleed
 * photos to the cut line and beyond. Keep text and logos well inside the trim edge ("safe zone").
 */
export async function elementToBleedPdfBytes(el: HTMLElement, trimWidthIn: number, bleedIn = 0.125): Promise<Uint8Array> {
  return pngToBleedPdfBytes(await elementToPngBlob(el, { printWidthIn: trimWidthIn }), trimWidthIn, bleedIn);
}

/** Same as above for a picture you already have (e.g. a canvas render): `trimWidthIn` is the finished width the picture is printed at. */
export async function pngToBleedPdfBytes(png: Blob, trimWidthIn: number, bleedIn = 0.125): Promise<Uint8Array> {
  const src = await png.arrayBuffer();
  const bmp = await createImageBitmap(new Blob([src], { type: "image/png" }));
  const L = bleedLayout(bmp.width, bmp.height, trimWidthIn, bleedIn);
  const b = L.bleedPx;
  const c = document.createElement("canvas");
  c.width = bmp.width + 2 * b; c.height = bmp.height + 2 * b;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  g.drawImage(bmp, b, b);
  const W = bmp.width, H = bmp.height;
  if (b > 0) {
    g.drawImage(bmp, 0, 0, 1, H, 0, b, b, H);               // left
    g.drawImage(bmp, W - 1, 0, 1, H, W + b, b, b, H);       // right
    g.drawImage(bmp, 0, 0, W, 1, b, 0, W, b);               // top
    g.drawImage(bmp, 0, H - 1, W, 1, b, H + b, W, b);       // bottom
    g.drawImage(bmp, 0, 0, 1, 1, 0, 0, b, b);               // corners
    g.drawImage(bmp, W - 1, 0, 1, 1, W + b, 0, b, b);
    g.drawImage(bmp, 0, H - 1, 1, 1, 0, H + b, b, b);
    g.drawImage(bmp, W - 1, H - 1, 1, 1, W + b, H + b, b, b);
  }
  const out = await new Promise<Blob>((res, rej) => c.toBlob((x) => (x ? res(x) : rej(new Error("canvas"))), "image/png"));
  const pdf = await PDFDocument.create();
  const img = await pdf.embedPng(new Uint8Array(await out.arrayBuffer()));
  const page = pdf.addPage([L.pageW, L.pageH]);
  page.drawImage(img, { x: 0, y: 0, width: L.pageW, height: L.pageH });
  page.setTrimBox(L.bleed, L.bleed, L.trimW, L.trimH);
  page.setBleedBox(0, 0, L.pageW, L.pageH);
  return pdf.save();
}

/** Pixels per inch a picture gets when printed `widthIn` wide. */
export const effectiveDpi = (pixelWidth: number, widthIn: number) => (widthIn > 0 ? pixelWidth / widthIn : 0);
